import PropTypes from 'prop-types'
import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useLocation } from 'react-router'
import findMatchedRoutes from 'utils/findMatchedRoutes'
import { authorizedRoutes, unAuthorizedRoutes } from 'constants/router'
import GridSpinner from 'components/UI/GridSpinner'
import { Container, PaymentLogo } from 'gu-ui-kit'
import styles from 'modules/Auth/authwrapper.module.scss'
import MainLayout from 'layouts/MainLayout'
import { Link, Route, Switch } from 'react-router-dom'
import TicketsCounterManager from 'components/TicketsCounterManager'
import SidebarNav from 'components/SidebarNav'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { LOGOUT, TRY_START_SESSION } from 'modules/Auth/types'
import { notificationShow } from 'modules/Notifications/actions'
import { Trans } from 'react-i18next'
import { IS_APP_PRODUCT_GETUNIQ } from 'utils/constants/product'
import TicketsPanel from 'components/TicketsPanel'

const AuthWrapper = () => {
  const dispatch = useDispatch()
  const { loading, auth } = useSelector(({ auth }) => auth)
  const { user } = useSelector(({ app }) => app)
  const pathGen = useGeneratePathI18n()
  const location = useLocation()
  const isUnauthorizedRoute = useMemo(
    () => !!findMatchedRoutes(unAuthorizedRoutes).length,
    [location],
  )

  const [refreshToken, setRefreshToken] = useState(
    localStorage.getItem('refresh_token'),
  )

  const onStorageHandler = ({ key, newValue }) =>
    key === 'refresh_token' && setRefreshToken(newValue || '')

  //handle if refresh token removed
  useEffect(() => {
    window.addEventListener('storage', onStorageHandler)

    return () => {
      window.removeEventListener('storage', onStorageHandler)
    }
  }, [])

  useEffect(() => {
    !isUnauthorizedRoute &&
      dispatch({
        type: localStorage.getItem('refresh_token')
          ? TRY_START_SESSION
          : LOGOUT,
      })
  }, [refreshToken, isUnauthorizedRoute])

  const renderRoutes = useMemo(
    () => (user?.id ? authorizedRoutes : unAuthorizedRoutes),
    [user?.id],
  )

  useEffect(() => {
    if (user?.is_email_confirmed === false) {
      dispatch(
        notificationShow({
          type: 'warning',
          message: (
            <Trans i18nKey="user::settings.profile.email.notification.disapproved">
              Подтвердите адрес электронной почты. Статус подтверждения можно
              смотреть в
              <Link to={pathGen(ROUTES.USER_SETTINGS)}>личном кабинете</Link>
            </Trans>
          ),
        }),
      )
    }
  }, [user])

  return !isUnauthorizedRoute && loading ? (
    <Container className={styles['loader']}>
      <div className={styles['logo-container']}>
        {IS_APP_PRODUCT_GETUNIQ ? (
          <>
            <PaymentLogo name={'getuniq'} color={'black'} />
            <GridSpinner width={16} color={'black'} />
          </>
        ) : (
          <>
            <PaymentLogo
              width={'40'}
              name={'adsimple'}
              color={'black'}
              className={styles['loader-logo']}
            />
            <GridSpinner width={24} color={'Interface-GU-Primary'} />
          </>
        )}
      </div>
    </Container>
  ) : (
    <>
      <Switch>
        {renderRoutes.map(({ layout, path, key }) => {
          const LayoutComponent =
            typeof layout === 'function'
              ? layout
              : layout === 'custom'
              ? React.Fragment
              : MainLayout
          return (
            <Route
              path={path}
              key={key}
              render={() => (
                <LayoutComponent>
                  <Suspense fallback={<GridSpinner />}>
                    {renderRoutes.map(
                      ({
                        component: RouteComponent = null,
                        key,
                        exact,
                        path,
                      }) => (
                        <Route
                          path={path}
                          key={'route-' + key}
                          exact={exact}
                          component={RouteComponent}
                        />
                      ),
                    )}
                  </Suspense>
                </LayoutComponent>
              )}
            />
          )
        })}
        <Route
          key={'no-match'}
          path={'*'}
          render={() => <Redirect to={pathGen(ROUTES.SERVICES)} />}
        />
      </Switch>
      {auth && (
        <>
          <TicketsCounterManager />
          <SidebarNav />
          <TicketsPanel />
        </>
      )}
    </>
  )
}

AuthWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),
}

export default AuthWrapper
